import React, { useEffect, useState } from "react";
import notificationService from "../service/notificationService";

/**
 * Custom hook for listening to Server-Sent Events (SSE) and automatically triggering refetch.
 *
 * @param transmissionQuery - The query object that has a `refetch` method.
 * @param threshold - Number of messages before triggering refetch.
 * @param refetchInterval - Interval (in ms) for periodic refetching (optional).
 */

export function useSSEWithRefetch({ requestQuery, threshold = 10, refetchInterval = 5000, watchEvents = [] }) {
  const [messages, setMessages] = useState([]);
  const [lastEventAt, setLastEventAt] = React.useState(null);
  const [now, setNow] = useState(Date.now());
  const [currentAction, setCurrentAction] = React.useState(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const eventSource = notificationService.listen(
      (data) => {
        if (watchEvents.length > 0 && !watchEvents.includes(data.event)) return;
        setMessages((prev) => [...prev, data]);
        setLastEventAt(Date.now());
        setCurrentAction(localStorage.getItem("action") || null);
      },
      (error) => {
        console.error("SSE connection error:", error);
      },
    );

    return () => eventSource.close();
  }, [watchEvents]);

  useEffect(() => {
    if (messages.length >= threshold) {
      requestQuery.refetch();
      setMessages([]);
    }
  }, [messages, threshold, requestQuery]);

  useEffect(() => {
    if (messages.length === 0) return;

    const timer = setTimeout(() => {
      if (messages.length > 0 && messages.length < threshold) {
        requestQuery.refetch();
        setMessages([]);
      }
    }, refetchInterval);

    return () => clearTimeout(timer);
  }, [messages, threshold, refetchInterval, requestQuery]);

  const isReceiving = !!lastEventAt && now - lastEventAt < 15000;

  return { messages, isReceiving, currentAction };
}
